import React, { useEffect, useState } from 'react';
import { Box, IconButton, List, ListItem, ListItemText, Typography } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import VideocamIcon from '@mui/icons-material/Videocam';
import { FormattedMessage } from 'react-intl';
import axiosConfig from 'src/Resources/axiosConfig';
import { ProductResource, isVideoResource } from 'src/interfaces/ProductResourceTypes';
import OnSiteVideoLightbox from '../OnSiteChecklist/OnSiteVideoLightbox';

interface Props {
  product: number;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function typeIcon(contentType: string) {
  if (contentType === 'application/pdf') return <PictureAsPdfIcon fontSize="small" color="error" />;
  if (contentType.startsWith('video/')) return <VideocamIcon fontSize="small" color="primary" />;
  if (contentType.startsWith('image/')) return <ImageIcon fontSize="small" color="action" />;
  return <InsertDriveFileIcon fontSize="small" />;
}

// Read-only list of admin-published, product-wide guidance files (general ones,
// i.e. NOT pinned to a checklist step — those render on the checklist). Shown to
// non-admin roles above their own installation documents. No delete/upload here.
function GuidanceResourceList({ product }: Props) {
  const [resources, setResources] = useState<ProductResource[]>([]);
  const [video, setVideo] = useState<ProductResource | null>(null);

  useEffect(() => {
    axiosConfig
      .get('/GetProductResources', { params: { product } })
      .then((res) => {
        const list: ProductResource[] = Array.isArray(res.data) ? res.data : [];
        setResources(list.filter((r) => r.targetStep == null));
      })
      .catch(() => setResources([]));
  }, [product]);

  const download = (r: ProductResource) => {
    axiosConfig
      .get('/DownloadDocument', { params: { id: r.id }, responseType: 'blob' })
      .then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', r.originalName);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {});
  };

  if (resources.length === 0) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
        <FormattedMessage id="documentHubGuidanceSection" defaultMessage="Guidance & Resources" />
      </Typography>
      <List dense disablePadding>
        {resources.map((r) => (
          <ListItem
            key={r.id}
            disableGutters
            secondaryAction={
              <Box sx={{ flexShrink: 0 }}>
                {isVideoResource(r) && (
                  <IconButton size="small" onClick={() => setVideo(r)}>
                    <PlayCircleOutlineIcon fontSize="small" />
                  </IconButton>
                )}
                <IconButton size="small" onClick={() => download(r)}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Box>
            }
          >
            <Box sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>{typeIcon(r.contentType)}</Box>
            <ListItemText
              primary={r.title || r.originalName}
              secondary={`${formatFileSize(r.sizeBytes)}${
                r.language ? ' · ' + r.language.toUpperCase() : ''
              }`}
              primaryTypographyProps={{ sx: { wordBreak: 'break-word', pr: 2 } }}
            />
          </ListItem>
        ))}
      </List>
      <OnSiteVideoLightbox resource={video} onClose={() => setVideo(null)} />
    </Box>
  );
}

export default GuidanceResourceList;
